/**
 * Placeholder generation helpers — extracted from NewsletterAIService.js
 * as part of #229 Wave 4 (the final wave of the decomposition sequence).
 *
 * This module holds the on-demand placeholder generation cluster plus
 * `generatePlaceholder` and `analyzeStyle` — two thin POSTs to the AI API
 * that share the same auth + budget + logger plumbing.
 *
 * NOTE: On-demand placeholder generation (`_generateAllPlaceholderValues`)
 * is the EXPENSIVE path; it is only enabled when
 * `forceExpensivePlaceholderGeneration: true` is passed. The default flow
 * uses pre-generated placeholders from S3 (Rule 17, 2x daily Lambda batch).
 *
 * Public exports:
 *   - generatePlaceholder(service, prompt) — service-bound. Thin POST,
 *     mode='placeholder', 100 tokens.
 *   - analyzeStyle(service, prompt) — service-bound. Thin POST,
 *     mode='analyze', 500 tokens.
 *   - generateAllPlaceholderValues(service, placeholders, context) —
 *     parallel batch helper that fans out per-placeholder LLM calls.
 *   - generatePlaceholderValue(service, placeholder, context) — single
 *     placeholder LLM call with fallback handling.
 *   - buildPlaceholderPrompt(placeholder, clientData, advisorPrefs, marketContext)
 *     — pure helper that builds the per-placeholder LLM prompt.
 *   - validateAndFormatPlaceholder(value, placeholder) — pure helper that
 *     trims/sanitises an LLM response into the placeholder's type contract.
 *   - getPlaceholderFallbackValue(placeholder, clientData) — pure helper
 *     used when LLM generation fails.
 */

import {
  createNetworkError,
} from '../../utils/StandardErrorFormat.js';

/**
 * Thin POST to the AI API for a single placeholder value.
 * Service-bound. Mode='placeholder', 100 maxTokens.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {string} prompt - The placeholder generation prompt
 * @returns {Promise<string>} The generated placeholder text (or '')
 */
export async function generatePlaceholder(service, prompt) {
  await service._assertWithinDailyBudget();  // M3 #151 P0 #1
  await service.initialize();

  try {
    const token = await service.getAuthToken();

    const response = await fetch(service.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      },
      body: JSON.stringify({
        prompt,
        mode: 'placeholder',
        maxTokens: 100
      })
    });

    if (!response.ok) {
      createNetworkError(`Placeholder generation failed: ${response.status}`);
    }

    const data = await response.json();
    return data.content || data.text || '';
  } catch (error) {
    service.logger.error('Placeholder generation error:', error);

    if (error.name === 'ServiceError') {
      throw error;
    }

    createNetworkError('Placeholder generation failed', error);
  }
}

/**
 * Analyze writing style from sample text via a thin POST to the AI API.
 * Service-bound. Mode='analyze', 500 maxTokens.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {string} prompt - The analysis prompt with sample text
 * @returns {Promise<string>} JSON string with the style analysis (or '{}')
 */
export async function analyzeStyle(service, prompt) {
  await service._assertWithinDailyBudget();  // M3 #151 P0 #1
  await service.initialize();

  try {
    const token = await service.getAuthToken();

    const response = await fetch(service.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      },
      body: JSON.stringify({
        prompt,
        mode: 'analyze',
        maxTokens: 500
      })
    });

    if (!response.ok) {
      createNetworkError(`Style analysis failed: ${response.status}`);
    }

    const data = await response.json();
    return data.content || data.text || '{}';
  } catch (error) {
    service.logger.error('Style analysis error:', error);

    if (error.name === 'ServiceError') {
      throw error;
    }

    createNetworkError('Style analysis failed', error);
  }
}

/**
 * Generate values for an array of placeholders in parallel.
 * Service-bound. Returns a map of placeholder key → generated value.
 * Individual failures are swallowed and replaced with the fallback value.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {Array<Object>} placeholders - Array of placeholder definitions
 * @param {Object} context - Shared context (clientData, advisorPreferences, marketContext, etc.)
 * @returns {Promise<Object>} Map of placeholder.key → generated string value
 */
export async function generateAllPlaceholderValues(service, placeholders, context) {
  const results = {};
  const promises = placeholders.map(async (placeholder) => {
    try {
      const value = await generatePlaceholderValue(service, placeholder, context);
      results[placeholder.key] = value;
    } catch (error) {
      service.logger.error(`Failed to generate ${placeholder.key}:`, error);
      results[placeholder.key] = placeholder.fallbackValue || '';
    }
  });
  await Promise.all(promises);
  return results;
}

/**
 * Generate a single placeholder value with fallback handling.
 * Service-bound.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {Object} placeholder - Placeholder definition (key, type, etc.)
 * @param {Object} context - Shared context
 * @returns {Promise<string>} The generated (or fallback) placeholder value
 */
export async function generatePlaceholderValue(service, placeholder, context) {
  const { clientData, advisorPreferences, marketContext } = context;
  const prompt = buildPlaceholderPrompt(placeholder, clientData, advisorPreferences, marketContext);
  try {
    const aiService = context.aiService || service;
    const result = await aiService.generatePlaceholder(prompt);
    return validateAndFormatPlaceholder(result, placeholder);
  } catch (error) {
    service.logger.error(`Failed to generate placeholder ${placeholder.key}:`, error);
    return getPlaceholderFallbackValue(placeholder, clientData);
  }
}

/**
 * Build the LLM prompt for a single placeholder. Pure function — no service deps.
 *
 * @param {Object} placeholder - Placeholder definition
 * @param {Object} clientData - Client information
 * @param {Object} advisorPrefs - Advisor writing preferences
 * @param {Object} marketContext - Market context object
 * @returns {string} The prompt text
 */
export function buildPlaceholderPrompt(placeholder, clientData, advisorPrefs, marketContext) {
  return `
Generate a value for the placeholder "${placeholder.key}" with the following context:

Placeholder Type: ${placeholder.type}
Placeholder Description: ${placeholder.description || 'N/A'}

Client Information:
- Name: ${clientData?.name || 'N/A'}
- Portfolio Value: ${clientData?.portfolioValue || 'N/A'}
- Risk Tolerance: ${clientData?.riskTolerance || 'N/A'}
- Investment Goals: ${clientData?.goals?.join(', ') || 'N/A'}

Writing Style Requirements:
- Style: ${advisorPrefs?.style || 'professional'}
- Tone: ${advisorPrefs?.tone || 'professional'}
- Complexity: ${advisorPrefs?.complexityLevel || 'intermediate'}

Market Context:
${marketContext?.prompt_injections?.MARKET_CONTEXT_COMBINED || marketContext?.combined_narrative || 'Current market data not available'}

Requirements:
- Keep response concise (${placeholder.maxLength || 100} characters max)
- Match the ${advisorPrefs?.style || 'professional'} writing style
- Use ${advisorPrefs?.complexityLevel || 'intermediate'} level language
- Be specific to client's situation
- Incorporate relevant market data

Generate only the placeholder value, no additional text.
    `.trim();
}

/**
 * Validate and format a raw LLM response into a typed placeholder value.
 * Pure function — no service deps.
 *
 * @param {string} value - Raw LLM output
 * @param {Object} placeholder - Placeholder definition (maxLength, type)
 * @returns {string} Cleaned + type-coerced value
 */
export function validateAndFormatPlaceholder(value, placeholder) {
  let cleaned = value.replace(/[*_`]/g, '').trim();
  if (placeholder.maxLength && cleaned.length > placeholder.maxLength) {
    cleaned = cleaned.substring(0, placeholder.maxLength - 3) + '...';
  }
  if (placeholder.type === 'number') {
    cleaned = cleaned.replace(/[^0-9.,]/g, '');
  } else if (placeholder.type === 'percentage') {
    cleaned = cleaned.replace(/[^0-9.%]/g, '');
  }
  return cleaned;
}

/**
 * Resolve a fallback value for a placeholder when LLM generation fails.
 * Pure function — no service deps.
 *
 * @param {Object} placeholder - Placeholder definition
 * @param {Object} clientData - Client information (may contain clientField)
 * @returns {string} The fallback value (clientField → fallbackValue → [key])
 */
export function getPlaceholderFallbackValue(placeholder, clientData) {
  if (placeholder.clientField && clientData?.[placeholder.clientField]) {
    return clientData[placeholder.clientField];
  }
  if (placeholder.fallbackValue) {
    return placeholder.fallbackValue;
  }
  return `[${placeholder.key}]`;
}
